// Equipment demand and supply - mirrors the API's api/equipment/equipment.demand.js.
//
// Demand is what a project asks for: "2 skid steers, 1-30 June" - one cost line of the
// plan with an `equipmentType` and a `quantity`, budgeted at the type's rates before
// anyone knows which machines will do the work. Supply is the machines: equipment
// bookings pointing back at the line (`booking.planItem`).

const DAY_MS = 86400000

// The units the line item maths prices (mf-utils.js#getLineItemCostAndRevenue and the
// API's financials.engine.js#addLineItems) - Hourly is per calendar day at the mission's
// hours per day. An equipment line prices its cost by `rateTime` and its revenue by
// `billUnit`; other cost lines price both by `rateTime`.
export const EQUIPMENT_LINE_UNITS = ["Hourly", "Daily", "Weekly", "Monthly", "One time"]

// What a line is priced in when its equipment type has no unit.
export const EQUIPMENT_LINE_DEFAULT_UNIT = "Hourly"

const round2 = (v) => Math.round(v * 100) / 100

export const isEquipmentLine = (planItem) => planItem?.type === "cost" && Boolean(planItem.equipmentType)

/**
 * Which ends of a line are pinned to the project's dates - the same `pinnedTo` values as
 * project roles ("start", "end", "startend" and the older "entireMission" / "startOfMission"
 * / "endOfMission"). App.js#missionUpdatePinnedItems moves pinned ends when the project's
 * dates change.
 *
 * @return {{ start: Boolean, end: Boolean }}
 */
export const getLinePins = (planItem = {}) => {
    const pinnedTo = planItem.pinnedTo || ""

    return {
        start: pinnedTo === "entireMission" || pinnedTo.includes("start"),
        end: pinnedTo === "entireMission" || pinnedTo.includes("end"),
    }
}

export const toPinnedTo = ({ start, end }) => (start && end ? "startend" : start ? "start" : end ? "end" : null)

/**
 * The rates a request starts with, from its equipment type: the cost rate in the type's
 * cost unit and the bill rate in its bill unit - Hourly when the type has none. Yearly
 * isn't a line unit, so a yearly rate becomes a twelfth of it, monthly.
 *
 * @return {{ rate, billRate, rateTime, billUnit }}
 */
export const getDemandRates = (type = {}) => {
    const lineUnit = (unit) =>
        unit === "Yearly" ? "Monthly" : EQUIPMENT_LINE_UNITS.includes(unit) ? unit : EQUIPMENT_LINE_DEFAULT_UNIT
    const lineRate = (amount, unit) => (unit === "Yearly" ? round2((amount || 0) / 12) : amount || 0)

    return {
        rate: lineRate(type.rate, type.rateTime),
        billRate: lineRate(type.billRate, type.billUnit),
        rateTime: lineUnit(type.rateTime),
        billUnit: lineUnit(type.billUnit),
    }
}

const floorDay = (ms) => Math.floor(ms / DAY_MS) * DAY_MS

/**
 * A request line's dates as booking days (end exclusive) - the API's getDemandDays.
 */
export const getDemandDays = (planItem = {}) => {
    const startDate = floorDay(planItem.startDate)
    return { startDate, endDate: Math.max(floorDay(planItem.endDate), startDate) + DAY_MS }
}

/**
 * Where a request stands, from the API's coverage ({ quantity, days, neededDays,
 * coveredDays, isFilled }): "open" (no machine yet), "partial" or "filled".
 *
 * @return {{ id: "open"|"partial"|"filled", label: String }}
 */
export const getDemandStatus = (line = {}) => {
    const quantity = line.quantity || 1
    const covered = line.coveredDays || 0

    if (line.isFilled) return { id: "filled", label: quantity === 1 ? "Assigned" : `All ${quantity} assigned` }
    if (!covered) return { id: "open", label: quantity === 1 ? "Not assigned" : `0 of ${quantity} assigned` }

    // Whole machines when the bookings cover the request end to end, machine-days when
    // they cover parts of it.
    return {
        id: "partial",
        label:
            line.days && covered % line.days === 0
                ? `${covered / line.days} of ${quantity} assigned`
                : `${covered} of ${line.neededDays} machine-days covered`,
    }
}
